import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicPagination from 'src/common/refactoredComponents/paginator';
import Select from 'src/common/refactoredComponents/select/Select';
import SortAscending from 'src/assets/images/SortAscending.svg';
import SortDescending from 'src/assets/images/SortDescending.svg';
import { PaginationContainerMobile } from 'src/common/organisms/productGuide/productSection/ProductSection.styled';
import { accountNumberZeroTrimmer } from 'src/utils/util';
import {
    Description,
    MobilePaginationWrapper,
    MobileSortRow,
    SetCard,
    SetCardBody,
    SetCardList,
    SetCardSection,
    SetNumber,
    StrongLabel,
    SubContent,
    SetBatchLink,
    MobileTableContainer,
} from './index.styled';
import {
    SETS_DEFAULT_SORT_CODE,
    SETS_PAGE_LIMIT,
    SETS_SORT_OPTIONS,
} from './setsInventoryConstants';
import { DISABLED_FONT_COLOR } from 'src/styles/fonts/MCFontConstants';

const compareValues = (a, b, ascending) => {
    const aVal = a ?? '';
    const bVal = b ?? '';
    const aNum = Number(aVal);
    const bNum = Number(bVal);
    const useNumeric =
        aVal !== '' && bVal !== '' && !Number.isNaN(aNum) && !Number.isNaN(bNum);
    let result;
    if (useNumeric) {
        result = aNum - bNum;
    } else {
        result = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }
    return ascending ? result : -result;
};
const getTextValue = (value) => (value === undefined || value === null ? '' : String(value).trim());

const getTrimmedAccountNumber = (value) => {
    const accountNumber = getTextValue(value);
    return accountNumber ? accountNumberZeroTrimmer(accountNumber.replace(/-/g, '')) : '';
};

const splitAccountValue = (value) => {
    const accountValue = getTextValue(value);
    const accountParts = accountValue.match(/^([0-9-]+)\s*-?\s*(.*)$/);

    if (accountParts) {
        return {
            accountName: getTextValue(accountParts[2]),
            accountNumber: getTrimmedAccountNumber(accountParts[1]),
        };
    }

    const trailingAccountParts = accountValue.match(/^(.*?)\s+([0-9-]+)$/);

    if (!trailingAccountParts) {
        return { accountName: accountValue, accountNumber: '' };
    }

    return {
        accountName: getTextValue(trailingAccountParts[1]),
        accountNumber: getTrimmedAccountNumber(trailingAccountParts[2]),
    };
};

const formatLocationAccount = (accountName, accountNumber, fallbackLocation) => {
    const trimmedName = getTextValue(accountName);
    const trimmedNumber = getTrimmedAccountNumber(accountNumber);

    if (trimmedName || trimmedNumber) {
        return [trimmedName, trimmedNumber].filter(Boolean).join(' ');
    }

    const parsedLocation = splitAccountValue(fallbackLocation);
    return [parsedLocation.accountName, parsedLocation.accountNumber].filter(Boolean).join(' ');
};

const SetsMobileTable = ({
    rows = [],
    totalRecords,
    recordsPerPage = SETS_PAGE_LIMIT,
    first = 0,
    onPageChange = () => {},
    handleBatchClick = () => {},
}) => {
    const { t } = useTranslation();
    const tableContainerRef = useRef(null);
    const pageNumber = Math.floor(first / recordsPerPage);
    const [sortCode, setSortCode] = useState(SETS_DEFAULT_SORT_CODE);
    const sortOptions = SETS_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
    }));


    const activeSort = useMemo(
        () => SETS_SORT_OPTIONS.find((opt) => opt.code === sortCode),
        [sortCode]
    );
    const sortIcon = activeSort?.ascending === false ? SortDescending : SortAscending;

    const sortedRows = useMemo(() => {
        if (!activeSort || !Array.isArray(rows)) {
            return rows;
        }
        return [...rows].sort((a, b) =>
            compareValues(a?.[activeSort.field], b?.[activeSort.field], activeSort.ascending)
        );
    }, [rows, activeSort]);

    const scrollTableToTop = () => {
        const container = tableContainerRef.current;

        if (!container) return;

        [container, container.querySelector('#receiveSets-setsInventory-mobile-list')]
            .forEach((scrollElement) => {
                if (scrollElement) {
                    scrollElement.scrollTop = 0;
                }
            });
        container.scrollIntoView({ block: 'start', behavior: 'auto' });
    };

    const handlePageChange = (event) => {
        scrollTableToTop();
        onPageChange(event);
    };

    return (
        <>  
            <MobileTableContainer ref={tableContainerRef}>
            <MobileSortRow id="receiveSets-setsInventory-mobile-sortRow">
                <Select
                    id="receiveSets-setsInventory-mobile-sortDropdown"
                    height="40px"
                    width="100%"
                    value={sortCode}
                    options={sortOptions}
                    optionValue="code"
                    optionLabel="label"
                    onChange={(value) => setSortCode(value)}
                    hideClear
                    selectedLabelFontSize="14px"
                    labelColor="#202124"
                    sortIcon={sortIcon}
                    isFromInventoryConsignment
                />
            </MobileSortRow>
            <SetCardList id="receiveSets-setsInventory-mobile-list">
                {(sortedRows || []).map((row) => (
                    <SetCard
                        key={row.id}
                        id={`receiveSets-setsInventory-mobile-card-${row.id}`}
                    >
                        <SetCardBody>
                            <SetCardSection>
                                {row.setBatch ? (
                                    <SetBatchLink
                                        href="#"
                                        onClick={(event) => handleBatchClick(event, row)}
                                    >
                                        {row.setNumber + "." + row?.setBatch}
                                    </SetBatchLink>) : (
                                    <SetNumber>{row.setNumber}</SetNumber>
                                )}
                                <Description>{row.description}</Description>
                                {getTextValue(row.brand) && (
                                    <SubContent>
                                        <StrongLabel>{t('receiveSets.setsInventory.fieldLabels.brand')}</StrongLabel> {row.brand}
                                    </SubContent>
                                )}
                            </SetCardSection>
                            <SetCardSection>
                                {row?.nextSurgerysstRefNo && (
                                <SubContent>
                                    <StrongLabel>{t(
                                        'receiveSets.setsInventory.fieldLabels.refNumber'
                                    )}</StrongLabel> {getTextValue(row.nextSurgerysstRefNo) || ''}
                                </SubContent>)}
                                {row?.nextSurgerySapOrder && (
                                <SubContent>
                                    <StrongLabel>{t('receiveSets.setsInventory.fieldLabels.orderNumber')} </StrongLabel> 
                                    {getTextValue(row.nextSurgerySapOrder) || ''}
                                </SubContent>)}
                            </SetCardSection>
                            <SetCardSection>
                                {row?.status && (
                                <SubContent>
                                    <StrongLabel>{t('receiveSets.setsInventory.fieldLabels.status')}</StrongLabel> {row.status}
                                </SubContent>)}
                            </SetCardSection>
                            <SetCardSection>
                                <SubContent>
                                    <StrongLabel>{t('receiveSets.setsInventory.fieldLabels.home')}</StrongLabel> {formatLocationAccount(row.homeAccName, row.homeAccNum, row.homeLocation)}
                                </SubContent>
                                <SubContent>
                                    <StrongLabel>{t('receiveSets.setsInventory.fieldLabels.current')}</StrongLabel> {formatLocationAccount(row.currentAccName, row.currentAccNum, row.currentLocation)}
                                </SubContent>
                            </SetCardSection>
                        </SetCardBody>
                        {/* <SetCardAction
                            id={`receiveSets-setsInventory-mobile-viewSet-${row.id}`}
                            type="button"
                            aria-label={t('receiveSets.setsInventory.viewSetAriaLabel', {
                                setNumber: row?.setNumber,
                            })}
                            onClick={() => onViewSet(row)}
                        >
                            <img src={ArrowRightBlue} alt="" />
                        </SetCardAction> */}
                    </SetCard>
                ))}
            </SetCardList>

            <MobilePaginationWrapper id="receiveSets-setsInventory-mobile-pagination-wrapper">
                <PaginationContainerMobile
                    id="receiveSets-setsInventory-mobile-pagination-container"
                    mobileBackground={'#FFFFFF'}
                    mobilePaginationWidth={'100%'}
                    mbMargin={'0px'}
                >
                    <BasicPagination
                        id="receiveSets-setsInventory-mobile-pagination"
                        totalRecords={totalRecords}
                        recordsPerPage={recordsPerPage}
                        pageNumber={pageNumber}
                        first={first}
                        handleChange={handlePageChange}
                        content={t('receiveSets.setsInventory.showingSets')}
                        background={'#FFFFFF'}
                    />
                </PaginationContainerMobile>
            </MobilePaginationWrapper>
            </MobileTableContainer>
        </>
    );
};

export default SetsMobileTable;
